import { Fragment, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { Badge } from 'shared/ui';

import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import type { ProjectMortgageRules } from '../hooks/useMortgageRules';
import {
  DEFAULT_PROMPT_OPTION,
  MORTGAGE_CONFIGURABLE_RULE_NAMES,
  MORTGAGE_VERTICAL_ENABLED,
  type ProjectRulesSnapshot,
  promptFieldForRule,
  readDs19Config,
  readPromptSectionName,
  ruleLabel,
} from '../model/mortgageRules';
import type { SubProjectPrompts } from './MortgageAgentPromptsCard';
import MortgageBulkActions from './MortgageBulkActions';
import MortgageDs19SubsidyCell from './MortgageDs19SubsidyCell';

/*
 * The selection and the name stay put while the table scrolls sideways, so a row can still be told
 * apart from the rest. They need an opaque background to cover what scrolls under them, and the row's
 * hover tint mixed into it by hand: the row paints a translucent tint that a solid cell would hide.
 */
const STICKY_CELL = 'sticky z-10 bg-bg-surface';
/* Spelled out twice because Tailwind only generates classes it finds written whole. The second one tints
   the row while its DS19 editor is open, since the popover trigger carries aria-expanded. */
const STICKY_ROW_CELL = cn(
  STICKY_CELL,
  'group-hover:bg-[color-mix(in_oklab,var(--color-muted)_50%,var(--color-bg-surface))]',
  'group-has-aria-expanded:bg-[color-mix(in_oklab,var(--color-muted)_50%,var(--color-bg-surface))]'
);
const STICKY_SELECT = 'left-0 w-10 min-w-10';
/* The name column closes the frozen block, and its edge marks where the scrolling part starts. */
const STICKY_NAME = 'left-10 shadow-[1px_0_0_0_var(--color-border)]';

interface Props {
  subProjects: SubProjectPrompts[];
  byProject: Map<number, ProjectMortgageRules>;
  countryCode: string | null | undefined;
}

/*
 * The rules are read on the sub-project, so this is where a master is configured: there is no row for
 * the master itself because nothing would read it. The table reports where each project stands; what
 * to change lives in the section below it.
 */
const MortgageSubProjectsMatrix = ({ subProjects, byProject, countryCode }: Props): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const selected = useMemo<ProjectRulesSnapshot[]>(
    () =>
      subProjects
        .filter((subProject) => selectedIds.has(subProject.id))
        .map((subProject) => {
          const entry = byProject.get(subProject.id);
          return entry ? { id: subProject.id, rules: entry.rules, isMultiAgent: entry.config.isMultiAgent } : null;
        })
        .filter((snapshot): snapshot is ProjectRulesSnapshot => snapshot !== null),
    [byProject, selectedIds, subProjects]
  );

  /* Seeded from the first selected project so the draft is edited rather than retyped from scratch. */
  const seedConfig = useMemo(() => {
    const first = subProjects.find((subProject) => selectedIds.has(subProject.id));
    return first ? byProject.get(first.id)?.config ?? null : null;
  }, [byProject, selectedIds, subProjects]);

  /* The DS19 is a Chilean programme: a Peruvian master has no amount to set. */
  const showsDs19 = countryCode === 'CL';

  const allSelected = subProjects.length > 0 && subProjects.every((subProject) => selectedIds.has(subProject.id));
  const someSelected = subProjects.some((subProject) => selectedIds.has(subProject.id));
  let headerCheckboxState: boolean | 'indeterminate' = false;
  if (allSelected) headerCheckboxState = true;
  else if (someSelected) headerCheckboxState = 'indeterminate';

  const toggleAll = (checked: boolean): void => {
    setSelectedIds(checked ? new Set(subProjects.map((subProject) => subProject.id)) : new Set());
  };

  const toggleRow = (id: number, checked: boolean): void => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return (
    <section className="space-y-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={cn(STICKY_CELL, STICKY_SELECT, 'pr-0')}>
              <Checkbox
                checked={headerCheckboxState}
                onCheckedChange={(checked) => toggleAll(checked === true)}
                aria-label="Seleccionar todos los subproyectos"
              />
            </TableHead>
            <TableHead className={cn(STICKY_CELL, STICKY_NAME)}>Subproyecto</TableHead>
            {MORTGAGE_CONFIGURABLE_RULE_NAMES.map((name) => {
              const promptField = promptFieldForRule(name);

              return (
                <Fragment key={name}>
                  <TableHead>{ruleLabel(name)}</TableHead>
                  {promptField && <TableHead className="text-text-muted">{promptField.shortLabel}</TableHead>}
                </Fragment>
              );
            })}
            <TableHead>Múltiples agentes</TableHead>
            {showsDs19 && (
              <TableHead title="Se configura por subproyecto y no se copia en los cambios en conjunto">
                Subsidio DS19
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {subProjects.map((subProject) => {
            const entry = byProject.get(subProject.id);

            return (
              <TableRow key={subProject.id} className="group">
                <TableCell className={cn(STICKY_ROW_CELL, STICKY_SELECT, 'pr-0')}>
                  <Checkbox
                    checked={selectedIds.has(subProject.id)}
                    onCheckedChange={(checked) => toggleRow(subProject.id, checked === true)}
                    aria-label={`Seleccionar ${subProject.name}`}
                  />
                </TableCell>
                <TableCell className={cn(STICKY_ROW_CELL, STICKY_NAME)}>
                  <span className="text-sm">{subProject.name}</span>
                  <span className="ml-2 text-xs text-text-muted">{subProject.id}</span>
                </TableCell>
                {MORTGAGE_CONFIGURABLE_RULE_NAMES.map((name) => {
                  /* Without the mode the agents never reach a conversation, so reporting them as
                     inactive would read as a setting rather than as the consequence it is. */
                  const notApplicable = name !== MORTGAGE_VERTICAL_ENABLED && !entry?.rules[MORTGAGE_VERTICAL_ENABLED];
                  const promptField = promptFieldForRule(name);
                  const sectionName = promptField ? readPromptSectionName(subProject[promptField.key]) : null;

                  return (
                    <Fragment key={name}>
                      <TableCell>
                        {notApplicable ? (
                          <span className="text-text-muted" title="No aplica sin el modo hipotecario">
                            —
                          </span>
                        ) : (
                          <Badge variant={entry?.rules[name] ? 'success' : 'default'}>
                            {entry?.rules[name] ? 'Activa' : 'Inactiva'}
                          </Badge>
                        )}
                      </TableCell>
                      {promptField && (
                        <TableCell>
                          <span
                            className={sectionName === DEFAULT_PROMPT_OPTION ? 'text-sm text-text-muted' : 'text-sm'}
                          >
                            {sectionName === DEFAULT_PROMPT_OPTION ? 'Por defecto' : sectionName}
                          </span>
                        </TableCell>
                      )}
                    </Fragment>
                  );
                })}
                <TableCell>
                  <Badge variant={entry?.config.isMultiAgent ? 'info' : 'default'}>
                    {entry?.config.isMultiAgent ? 'Múltiples' : 'Legacy'}
                  </Badge>
                </TableCell>
                {showsDs19 && (
                  <TableCell>
                    <MortgageDs19SubsidyCell
                      projectId={subProject.id}
                      projectName={subProject.name}
                      {...readDs19Config(subProject.subsidyConfig)}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MortgageBulkActions
        selectedIds={[...selectedIds]}
        selected={selected}
        subProjects={subProjects}
        countryCode={countryCode}
        seedConfig={seedConfig}
        onApplied={() => setSelectedIds(new Set())}
      />
    </section>
  );
};

export default MortgageSubProjectsMatrix;
